import { Box, Stack, Tooltip } from "@mui/material";

import { HeatEvaluationCode, SupplierHeatRow } from "../models/ChemistryDraft";

export type EvaluationTone =
	| "noGrade"
	| "out"
	| "missing"
	| "in"
	| "noRange"
	| "revision"
	| "neutral";

export const EVALUATION_TONES: Record<
	EvaluationTone,
	{ color: string; bgcolor: string; dot: string }
> = {
	noGrade: { color: "#AA2424", bgcolor: "#FAE5E5", dot: "#CF3333" },
	out: { color: "#AE4515", bgcolor: "#FAECDC", dot: "#D65E1D" },
	missing: { color: "#7D5614", bgcolor: "#F9F4D0", dot: "#BD8E19" },
	in: { color: "#227542", bgcolor: "#E1F6E8", dot: "#309B59" },
	noRange: { color: "#6439B2", bgcolor: "#EDEAF9", dot: "#7F55D2" },
	revision: { color: "#874917", bgcolor: "#F9EFD3", dot: "#C4781D" },
	neutral: { color: "#4B5563", bgcolor: "#F3F4F6", dot: "#9CA3AF" },
};

const NOT_EVALUATED: Partial<
	Record<HeatEvaluationCode, { label: string; tone: EvaluationTone }>
> = {
	NO_SPEC: { label: "No spec", tone: "noRange" },
	NO_CHEMISTRY: { label: "No chemistry", tone: "neutral" },
};

const ToneChip = ({
	label,
	tone,
	title,
	dashed = false,
}: {
	label: string;
	tone: EvaluationTone;
	title?: string;
	dashed?: boolean;
}) => {
	const { color, bgcolor, dot } = EVALUATION_TONES[tone];
	const chip = (
		<Box
			component="span"
			sx={{
				display: "inline-flex",
				alignItems: "center",
				height: 22,
				px: 1.25,
				borderRadius: 999,
				fontSize: 11,
				fontWeight: dashed ? 600 : 500,
				lineHeight: 1,
				whiteSpace: "nowrap",
				color,
				bgcolor,
				border: "1px dashed",
				borderColor: dashed ? dot : "transparent",
			}}
		>
			{label}
		</Box>
	);

	return title ? (
		<Tooltip title={title} arrow>
			{chip}
		</Tooltip>
	) : (
		chip
	);
};

export const EvaluationChips = ({
	heat,
	compact = false,
}: {
	heat: SupplierHeatRow;
	compact?: boolean;
}) => {
	if (heat.EvaluationCode === "NO_GRADE")
		return (
			<ToneChip
				label="No grade detected"
				tone="noGrade"
				title="The grade printed on the certificate could not be resolved to a plant grade"
				dashed
			/>
		);

	const notEvaluated = NOT_EVALUATED[heat.EvaluationCode];
	if (notEvaluated)
		return (
			<ToneChip label={notEvaluated.label} tone={notEvaluated.tone} dashed />
		);

	const inRangeCount =
		heat.LimitCount - heat.OutOfRangeCount - heat.MissingCount;

	const parts: {
		key: string;
		count: number;
		label: string;
		title: string;
		tone: EvaluationTone;
	}[] = [
		{
			key: "out",
			count: heat.OutOfRangeCount,
			label: compact ? "out" : "out of range",
			title: "Elements outside the grade range",
			tone: "out",
		},
		{
			key: "missing",
			count: heat.MissingCount,
			label: "missing",
			title: "Elements with a grade range but no reported value",
			tone: "missing",
		},
		{
			key: "in",
			count: inRangeCount,
			label: compact ? "in" : "in range",
			title: "Elements within the grade range",
			tone: "in",
		},
		{
			key: "without",
			count: heat.WithoutRangeCount,
			label: compact ? "no range" : "without range",
			title: "Reported elements the grade has no range for",
			tone: "noRange",
		},
	];
	const visibleParts = parts.filter((part) => part.count > 0);

	if (visibleParts.length === 0) return <>-</>;

	return (
		<Stack direction="row" spacing={0.5} alignItems="center">
			{visibleParts.map((part) => (
				<ToneChip
					key={part.key}
					tone={part.tone}
					label={`${part.count} ${part.label}`}
					title={compact ? part.title : undefined}
				/>
			))}
		</Stack>
	);
};
